import React, {
  useRef,
  useEffect,
  useState,
  useCallback
} from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-emoji/dist/quill-emoji.css';
import 'quill-emoji';
import { useTranslation } from 'react-i18next';
import { urlSerializer } from 'utils/utils';
import {
  countUnits,
  formatBioCount,
  markOverflowHtml,
} from 'utils/bio-limit';
import { RESUME_BIO_UNIT } from 'constants/options';

/**
 * Overflow shading is painted by a mirror element, not by a Quill format.
 *
 * A format lives in the document, so every silent formatText reached
 * react-quill through its `editor-change` binding (which exists precisely to
 * pick up silent updates), came back out as onChange, and diverged the stored
 * bio from the editor HTML — one shaded, one not. moreinfo re-seeds the field
 * from the store, so the two never converged and the tab hung.
 *
 * The mirror renders the same HTML behind a transparent-text editor, so Quill's
 * document is never touched and there is no value to diverge.
 */

export default function WYSIWYGEditor({
  label = '',
  labelClass = 'text-sm font-semibold text-darkerGray',
  inputClass = '',
  fullToolbar = false,
  optional = false,
  onChange = () => {},
  onSelect = () => {},
  limitChar = 10000,
  limitUnit = RESUME_BIO_UNIT,
  overflowShading = false,
  // Suppress this editor's own character counter. Used when a shared counter
  // lives outside the editor (the resume's pooled budget, MCF-637). Shading is
  // unaffected — only the counter UI is hidden.
  hideCounter = false,
  minimalToolBar = false,
  onBlur = ()=>{},
  isResume = false,
  ...props
}) {
  const quillRef = useRef(null);
  
  // --- old-editor state ---
  const [remainingChars, setRemainingChars] = useState(1);
  const [usedUnits, setUsedUnits] = useState(0);
  const [hideCutText, setHideCutText] = useState(false);
  const mirrorRef = useRef(null);
  const hostRef = useRef(null);
  const maxLines = 10;
  const { t } = useTranslation();

  // --- mention state & refs ---
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const mentionPosRef = useRef(null);
  const lastRangeRef = useRef(null);
  const suggestionsRef = useRef([]);
  const activeIndexRef = useRef(0);
  const isSelectingRef = useRef(false);
  const debounceRef = useRef(null);

  // keep refs in sync
  useEffect(() => { suggestionsRef.current = suggestions; }, [suggestions]);
  useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

  // --- clipboard matcher & initial char count (old code) ---
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Replace <a> with href text, unless it's a mention
    quill.format("color", "#000000");
    // quill.clipboard.addMatcher('A', (node, delta) => {
    //   const href = node.getAttribute('href') || '';
    //   const txt = node.innerText || '';
    //   if (txt.startsWith('@')) return delta;
    //   return { ops: [{ insert: href || txt }] };
    // });

    // set initial character count
    setRemainingChars(quill.getLength());
  }, []);

  useEffect(() => {
    if(!isResume) return;
    const quill = quillRef.current?.getEditor();

    if (!quill || !props.value) return;
      const text = quill.getText().trim();

      // Count emojis correctly
      const textLength = Array.from(text).length;

      // // Limit chars
      // if (textLength > limitChar) {
      //   quill.deleteText(limitChar, quill.getLength());
      // }

      setRemainingChars(textLength);

      // onChange(text);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, limitChar]);

  // --- fetch profiles for @mention (new code) ---
  const fetchProfiles = useCallback(async term => {
    // const cleaned = term.replace(/\s+/g, ' ').trim();
    // const params = { page: 1, keyword: JSON.stringify(cleaned) };
    // const url = await urlSerializer('public/profiles/search-tag', params);
    // const resp = await global.services.http.get(url);
    // return resp.data.data;
    return []
  }, []);

  // const hideSuggestions = useCallback(() => {
  //   setSuggestions([]);
  //   setActiveIndex(0);
  // }, []);

  // const resetMention = useCallback(() => {
  //   mentionPosRef.current = null;
  //   hideSuggestions();
  // }, [hideSuggestions]);

  // const updateSuggestions = useCallback(async term => {
  //   if (mentionPosRef.current == null) return;
  //   const results = await fetchProfiles(term);
  //   if (!results.length) {
  //     hideSuggestions();
  //     return;
  //   }
  //   setSuggestions(results);
  //   setActiveIndex(0);

  //   // position the popup
  //   const quill = quillRef.current.getEditor();
  //   const sel = quill.getSelection();
  //   if (!sel) return;
  //   const bounds = quill.getBounds(sel.index);
  //   setCoords({ top: bounds.top + bounds.height, left: bounds.left });
  // }, [fetchProfiles, hideSuggestions]);

  // const debouncedUpdate = useCallback(term => {
  //   clearTimeout(debounceRef.current);
  //   debounceRef.current = setTimeout(() => {
  //     updateSuggestions(term);
  //   }, 300);
  // }, [updateSuggestions]);

  // const selectItem = useCallback(idx => {
  //   const quill = quillRef.current.getEditor();
  //   const pos = mentionPosRef.current;
  //   if (pos == null) {
  //     hideSuggestions();
  //     return;
  //   }
  //   isSelectingRef.current = true;
  //   const item = suggestionsRef.current[idx];
  //   const range = lastRangeRef.current || quill.getSelection();
  //   const length = range.index - pos;

  //   // remove the "@term", insert "@username "
  //   quill.deleteText(pos, length, 'user');
  //   quill.insertText(pos, `@${item.username} `, { color: 'blue' }, 'user');
  //   quill.setSelection(pos + item.username.length + 2, 0);
  //   quill.format('color', false);

  //   // finish
  //   resetMention();
  //   onSelect(item);

  //   // allow further typing
  //   setTimeout(() => {
  //     isSelectingRef.current = false;
  //     quill.format('color', false);
  //   }, 0);
  // }, [hideSuggestions, onSelect, resetMention]);

  // --- bind text-change & keydown for both old & new behaviors ---
  // useEffect(() => {
  //   const quill = quillRef.current?.getEditor();
  //   if (!quill) return;

  //   const onTextChange = (delta, oldDelta, source) => {
  //     if (isSelectingRef.current) return;
  //     if (source !== 'user') return;

  //     // track selection
  //     const range = quill.getSelection(true);
  //     if (!range) return;
  //     lastRangeRef.current = range;

  //     // --- mention logic ---
  //     const fullText = quill.getText(0, range.index);
  //     const inserted = delta.ops
  //       .map(op => (typeof op.insert === 'string' ? op.insert : ''))
  //       .join('');
  //     if (inserted === '@') {
  //       mentionPosRef.current = range.index - 1;
  //       debouncedUpdate('');
  //     } else if (mentionPosRef.current != null) {
  //       const term = fullText.slice(mentionPosRef.current + 1, range.index);
  //       if (/^\w*$/.test(term)) {
  //         debouncedUpdate(term);
  //       } else {
  //         hideSuggestions();
  //       }
  //     }

  //     // --- character & line limits (old code) ---
  //     const limit = limitChar;
  //     const ops = quill.getContents().ops;
  //     const lineCount = ops
  //       .map(op =>
  //         typeof op.insert === 'string'
  //           ? op.insert.split('\n').length - 1
  //           : 0
  //       )
  //       .reduce((a, b) => a + b, 0);

  //     if (quill.getLength() > limit) {
  //       quill.deleteText(limit, quill.getLength());
  //     }
  //     if (lineCount > maxLines) {
  //       quill.deleteText(quill.getSelection()?.index - 1, 1);
  //     } else {
  //       setRemainingChars(quill.getLength());
  //     }
  //   };

  //   const onKeyDown = e => {
  //     if (!suggestionsRef.current.length) return;
  //     if (e.key === 'ArrowDown') {
  //       e.preventDefault();
  //       setActiveIndex(i => (i + 1) % suggestionsRef.current.length);
  //     } else if (e.key === 'ArrowUp') {
  //       e.preventDefault();
  //       setActiveIndex(i =>
  //         (i - 1 + suggestionsRef.current.length) %
  //         suggestionsRef.current.length
  //       );
  //     } else if (e.key === 'Enter') {
  //       e.preventDefault();
  //       selectItem(activeIndexRef.current);
  //     } else if (e.key === 'Escape') {
  //       e.preventDefault();
  //       hideSuggestions();
  //     }
  //   };

  //   quill.on('text-change', onTextChange);
  //   quill.root.addEventListener('keydown', onKeyDown);
  //   return () => {
  //     quill.off('text-change', onTextChange);
  //     quill.root.removeEventListener('keydown', onKeyDown);
  //   };
  // }, [
  //   debouncedUpdate,
  //   hideSuggestions,
  //   selectItem,
  //   limitChar,
  //   maxLines
  // ]);

  // --- overflow shading (mirror) --------------------------------------------

  /**
   * Repaint the mirror from the editor's own HTML and keep it aligned.
   *
   * The mirror carries Quill's own `ql-container`/`ql-editor` classes, so font,
   * padding, line-height and wrapping come from one stylesheet rather than a
   * copied list that could drift. Position and size are measured off the real
   * editor each sync, so a growing field stays covered.
   */
  const syncMirror = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Quill keeps a trailing newline that is not part of the content.
    const raw = quill.getText();
    const body = raw.endsWith(String.fromCharCode(10)) ? raw.slice(0, -1) : raw;
    setUsedUnits(countUnits(body, limitUnit));

    const mirror = mirrorRef.current;
    const host = hostRef.current;
    if (!overflowShading || !mirror || !host) return;

    const root = quill.root;
    mirror.innerHTML = markOverflowHtml(root.innerHTML, limitChar, limitUnit);

    const box = mirror.parentElement;
    const editorBox = root.getBoundingClientRect();
    const hostBox = host.getBoundingClientRect();
    box.style.top = editorBox.top - hostBox.top + 'px';
    box.style.left = editorBox.left - hostBox.left + 'px';
    box.style.width = editorBox.width + 'px';
    box.style.height = editorBox.height + 'px';

    mirror.scrollTop = root.scrollTop;
    mirror.scrollLeft = root.scrollLeft;

  }, [overflowShading, limitChar, limitUnit]);

  // Repaint when the bio arrives from the profile, or the template changes.
  useEffect(() => {
    if (!overflowShading) return;
    syncMirror();
  }, [overflowShading, props.value, limitChar, limitUnit, syncMirror]);

  // Keep the mirror's scroll locked to the editor's.
  useEffect(() => {
    if (!overflowShading) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root = quill.root;
    const onScroll = () => {
      const mirror = mirrorRef.current;
      if (!mirror) return;
      mirror.scrollTop = root.scrollTop;
      mirror.scrollLeft = root.scrollLeft;
    };
    root.addEventListener('scroll', onScroll);
    return () => root.removeEventListener('scroll', onScroll);
  }, [overflowShading]);

  // --- toolbars & formats ---
  const modulesBasic = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'bullet' }], ['emoji']],
    clipboard: { matchVisual: false },
    'emoji-toolbar': true,
    'emoji-textarea': false,
    'emoji-shortname': true,
  };

  const minimalModule = {
    toolbar: [['bold', 'italic', 'underline']],
  };

  const modulesFull = {
    toolbar: [
      [{ header: '1' }, { header: '2' }, { font: [] }],
      [{ size: [] }],
      ['bold','italic','underline','strike','blockquote'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ],
      ['image','video'],
      ['clean']
    ],
    clipboard: { matchVisual: false },
    'emoji-toolbar': true,
    'emoji-textarea': false,
    'emoji-shortname': true,
  };

  const formats = [
    'header','font','size',
    'bold','italic','underline','strike','blockquote',
    'list','bullet','indent',
    'link','image','video',
    'emoji','color'
  ];

  // --- onChange handler ---
  const handleChange = html => {
    // Forward exactly what Quill produced. Nothing is added to the value, so the
    // stored bio and the editor cannot drift apart.
    onChange(html);
    // Keep the character counter live as the user types. Without this the
    // non-overflow counter only refreshed on re-render, so the number appeared
    // stuck until you blurred/returned to the field.
    const quill = quillRef.current?.getEditor();
    if (quill) setRemainingChars(quill.getLength());
    if (overflowShading) syncMirror();
  };

  // --- counter -------------------------------------------------------------
  // Under the limit is quiet, at the limit is black (nothing is wrong yet),
  // over the limit is red and names the consequence.
  const isOver = usedUnits > limitChar;
  const isAtLimit = usedUnits === limitChar;
  const usedPercent =
    limitChar > 0 ? Math.min(100, Math.round((usedUnits / limitChar) * 100)) : 100;
  const counterTone = isOver
    ? 'text-bioCounterOver'
    : isAtLimit
    ? 'text-bioCounterAt'
    : 'text-gray-600';
  const barTone = isOver
    ? 'bg-bioCounterOver'
    : isAtLimit
    ? 'bg-bioCounterAt'
    : 'bg-bioCounterUnder';
  const counterNote = isOver
    ? `${formatBioCount(usedUnits - limitChar)} ${t('bioCounterUnused')}`
    : isAtLimit
    ? t('bioCounterAtLimit')
    : `${formatBioCount(limitChar - usedUnits)} ${t('bioCounterLeft')}`;

  return (
    <div
      className={`form-input-container ${overflowShading ? 'bio-overflow-field' : ''} ${
        overflowShading && isOver ? 'bio-overflow-active' : ''
      } ${hideCutText ? 'bio-overflow-hidden' : ''}`}
      style={{ width: '100%', position: 'relative' }}
    >
      {label && (
        <label htmlFor={label} className={labelClass}>
          {label}
          {optional && (
            <span className="font-normal">
              {' '}({t('optional')})
            </span>
          )}
        </label>
      )}

      <div className="relative" ref={hostRef}>
        <ReactQuill
          ref={quillRef}
          className={`mt-2 w-full ${inputClass}`}
          theme="snow"
          modules={fullToolbar ? modulesFull : modulesBasic}
          formats={formats}
          onChange={handleChange}
          onBlur={onBlur}
          {...props}
        />

        {/*
          Paints the shading behind a transparent-text editor. Carries Quill's own
          classes so it inherits identical type metrics, and is inert to both the
          pointer and the accessibility tree — the real editor stays the only
          focusable, screen-reader-visible element.
        */}
        {overflowShading && (
          <div className="bio-overflow-mirror ql-container ql-snow" aria-hidden="true">
            <div className="ql-editor" ref={mirrorRef} />
          </div>
        )}
      </div>

      {overflowShading ? (
        <>
          {/* Restates the rule at the point of confusion. */}
          {isOver && (
            <div className="flex items-center justify-between gap-x-3 px-4 py-2.5 border border-t-0 border-gray-300 rounded-b-md bg-gray-50">
              <span className="text-xs text-darkerGray">
                {t('bioOverflowFooter')}
              </span>
              <button
                type="button"
                onClick={() => setHideCutText(value => !value)}
                className="text-xs font-semibold text-bioCounterUnder whitespace-nowrap"
              >
                {hideCutText ? t('bioShowCutText') : t('bioHideCutText')}
              </button>
            </div>
          )}

          {!hideCounter && (
          <div className="mt-3 flex items-center justify-between gap-x-4">
            <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className={`h-full ${barTone}`}
                style={{ width: `${usedPercent}%` }}
              />
            </div>
            <div className="flex items-baseline gap-x-2 whitespace-nowrap">
              <span className={`text-sm font-medium ${counterTone}`}>
                {formatBioCount(usedUnits)} / {formatBioCount(limitChar)}
              </span>
              <span className="text-xs text-gray-500">{counterNote}</span>
            </div>
          </div>
          )}
        </>
      ) : (
        /* remaining-character counter */
        !hideCounter && (
        <div
          className={`text-sm font-semibold flex justify-end ${
            remainingChars > limitChar
              ? 'text-[#fd2727]'
              : 'text-gray-700'
          }`}
        >
          {Math.abs(remainingChars - 1)} / {limitChar}
        </div>
        )
      )}

      {/* mention dropdown -- temporary remove the suggestion*/} 
      {/* {suggestions.length > 0 && (
        <div
          className="mention-list"
          style={{
            position: 'absolute',
            top: coords.top,
            left: coords.left,
            zIndex: 1000
          }}
        >
          {suggestions.map((item, idx) => (
            <div
              key={item.id || item.username}
              className={`mention-item${
                idx === activeIndex ? ' active' : ''
              }`}
              onMouseDown={() => selectItem(idx)}
            >
              {item.username}
            </div>
          ))}
        </div>
      )} */}
    </div>
  );
}
